import React, { useContext, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import styles from './CreateAccount.module.scss';
import { ReactComponent as LogoImage } from '../../../images/logo-full.svg';
import accountImage from '../../../images/create-account.png';
import { createUser, REGISTRATIONS_PATH, UserInDTO } from '../../../services/api';
import { LoadingContext } from '../../../contexts/LoadingContext';
import {
  handleError,
  getFieldError,
  getApiErrorCode,
  isTurnstileTokenSpent,
  ApiErrorCode,
} from '../../../utils/errorUtils';
import { validateOptionalPhone } from '../../../utils/phoneUtils';
import { getAppToken, refreshAppToken } from '../../../utils/appToken';
import PhoneField from '../../../components/inputs/PhoneField/PhoneField';
import { identifyUser, captureEvent } from '../../../services/analytics';
import AuthFooter from '../../../components/auth/AuthFooter';
import TurnstileWidget, {
  TurnstileWidgetHandle,
  TURNSTILE_MESSAGE_ID,
} from '../../../components/auth/TurnstileWidget';

// The one inline message slot under the widget. Named for the slot, not for
// Turnstile: it carries the "you have not solved it yet" prompt, the rejection,
// and the service-fault message below, which is not about the user's widget at
// all.
const SUBMIT_ERROR_ID = 'create-account-submit-error';

// Shown when the user submits with no token. The button stays enabled on
// purpose — a content blocker leaves the token permanently empty, and a
// permanently disabled button explains nothing — so this message is the whole
// explanation the user gets for the click that did nothing.
const TURNSTILE_REQUIRED_MESSAGE =
  'Please complete the verification check above, then try again.';

// Shown when the backend rejected the token (403 turnstile_verification_failed).
// Deliberately does not promise a challenge to solve: in managed mode the reset
// usually re-issues a token silently and there is nothing to do but resubmit. It
// also has to stay compatible with TURNSTILE_REQUIRED_MESSAGE, which is what a
// user sees if they resubmit before the new token lands — one retry, one
// coherent instruction.
const TURNSTILE_REJECTED_MESSAGE =
  "We couldn't confirm that verification. The check above has been refreshed — please complete it if prompted, then try again.";

// Shown when the 403 came from the APP-credential login rather than from the
// user's own signup POST — the backend's exempt-username allowlist is broken.
// Nothing the user does can fix it and no amount of re-solving will help, so the
// copy must not mention their verification: the previous wording ("We could not
// verify this request. Please try again.") sent them into an unwinnable retry
// loop over a fault on our side.
const SIGNUP_UNAVAILABLE_MESSAGE =
  "We can't create accounts right now — this is a problem on our side, not with your details. Please try again later, or email info@jobbeast.org.";

const TERMS_URL = 'https://jobbeast.org/terms-of-service';
const PRIVACY_URL = 'https://jobbeast.org/privacy-policy';

/** Small info affordance shown beside an optional field's label. */
const FieldInfo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Tooltip title={children} trigger={['hover', 'focus', 'click']}>
    <button type="button" className={styles.infoButton} aria-label={label}>
      <InfoCircleOutlined aria-hidden />
    </button>
  </Tooltip>
);

const CreateAccount: React.FC = () => {
  const navigate = useNavigate();
  const { setIsLoading } = useContext(LoadingContext);
  const [phone, setPhone] = useState<string>();
  const [phoneError, setPhoneError] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const createNewUser = async (
    userData: UserInDTO,
    appToken: string,
    turnstileToken: string,
  ): Promise<void> => {
    try {
      const createUserResponse = await createUser(userData, appToken, turnstileToken);
      localStorage.setItem('userId', createUserResponse.id);
      const emailDomain = userData.username.split('@')[1];
      identifyUser(createUserResponse.id, { email_domain: emailDomain });
      captureEvent('user_signed_up', {
        signup_method: 'email',
        has_phone: !!userData.phone,
        has_linkedin: !!userData.linkedinUrl,
        email_domain: emailDomain,
      });
      console.debug("Verification URL:");
      console.debug(createUserResponse.verificationURL);
      navigate('/registration/account-created');
    } catch (error) {
      if (
        error instanceof Error &&
        (error as any).response &&
        (error as any).response.status === 401
      ) {
        const newAppToken = await refreshAppToken();

        // Same Turnstile token on the retry: a 401 is the app credential being
        // stale, which the backend answers before it ever looks at the body, so
        // the token has not been redeemed. If that ever stops holding, the retry
        // comes back as turnstile_verification_failed and handleSubmit resets
        // the widget — the user retries by hand, which is the safe direction.
        await createNewUser(userData, newAppToken, turnstileToken);
      } else {
        throw error;
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const phoneCheck = validateOptionalPhone(phone);
    if (!phoneCheck.ok) {
      setPhoneError(phoneCheck.error);
      return;
    }

    // Refuse to POST without a token. The backend would answer 403, we would
    // reset the widget, and an unsolvable widget (blocked script) would put the
    // user in a 403 -> reset -> 403 loop; stopping here makes that impossible.
    const turnstileToken = turnstileRef.current?.getToken() ?? '';
    if (!turnstileToken) {
      setSubmitError(TURNSTILE_REQUIRED_MESSAGE);
      return;
    }
    setSubmitError(undefined);

    setIsLoading(true);

    try {
      const appToken = await getAppToken();

      const formData = new FormData(form);
      const userData: UserInDTO = {
        username: formData.get('email') as string,
        firstName: formData.get('firstName') as string,
        lastName: formData.get('lastName') as string,
        password: formData.get('password') as string,
      };

      if (phoneCheck.value) {
        userData.phone = phoneCheck.value;
      }

      const linkedinAddress = formData.get('linkedinAddress') as string;
      if (linkedinAddress) {
        userData.linkedinUrl = linkedinAddress;
      }

      await createNewUser(userData, appToken, turnstileToken);
    } catch (error) {
      // Scoped to the registrations POST, because it is not the only request in
      // this try: getAppToken() -> refreshAppToken() -> login() runs here too,
      // with no token, relying on the backend's exempt-username allowlist. If
      // that allowlist is ever broken, THAT login answers 403
      // turnstile_verification_failed — the same code, about a completely
      // different token. Acting on it would reset a perfectly good widget and
      // tell a blameless user their verification failed, forever, while the real
      // fault (registration, password reset and the contact form all down) went
      // unnamed.
      const fromRegistration = (error as any)?.config?.url === REGISTRATIONS_PATH;

      // Two separate questions: is the token spent (reset), and what do we tell
      // the user (below). /users/registrations verifies BEFORE it writes the
      // user, so a 409 duplicate email or a 422 phone error has already redeemed
      // the token — not resetting would make the corrected resubmit fail too.
      // Never auto-resubmit after a reset: managed mode may escalate a
      // previously silent user to an interactive challenge, so the user acts.
      if (fromRegistration && isTurnstileTokenSpent(error)) {
        turnstileRef.current?.reset();
      }

      if (getApiErrorCode(error) === ApiErrorCode.TurnstileVerificationFailed) {
        // Same error_code, two completely different faults — and only the first
        // is the user's to act on.
        setSubmitError(fromRegistration ? TURNSTILE_REJECTED_MESSAGE : SIGNUP_UNAVAILABLE_MESSAGE);
        return;
      }
      const fieldError = getFieldError(error, 'phone');
      if (fieldError) {
        setPhoneError(fieldError);
      } else {
        handleError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        <a href="https://jobbeast.org" target="_blank" rel="noopener noreferrer">
          <LogoImage className={styles.logo} />
        </a>
      </div>
      <div className={styles.formContainer}>
        <h1>Create an Account</h1>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="firstName">First Name *</label>
              <input type="text" id="firstName" name="firstName" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="lastName">Last Name *</label>
              <input type="text" id="lastName" name="lastName" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email *</label>
              <input type="email" id="email" name="email" required />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">Password *</label>
              <div className={styles.passwordWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  required
                />
                <button
                  type="button"
                  className={styles.revealToggle}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeInvisibleOutlined aria-hidden /> : <EyeOutlined aria-hidden />}
                </button>
              </div>
            </div>
            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label htmlFor="phone">Phone (optional)</label>
                <FieldInfo label="Why we ask for your phone number">
                  Optional. Added to your email signature so employers can call or text you.
                </FieldInfo>
              </div>
              <PhoneField
                id="phone"
                value={phone}
                onChange={(v) => { setPhone(v); setPhoneError(undefined); }}
                error={phoneError}
              />
            </div>
            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label htmlFor="linkedinAddress">LinkedIn Profile Address (optional)</label>
                <FieldInfo label="Why we ask for your LinkedIn profile">
                  Optional. Added to your email signature so employers can view your profile.
                </FieldInfo>
              </div>
              <input type="text" id="linkedinAddress" name="linkedinAddress" />
            </div>
          </div>
          <div className={styles.actions}>
            <label className={styles.consentCheck}>
              <input type="checkbox" name="acceptTerms" required />
              <span>
                I agree to the{' '}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>.
              </span>
            </label>
            <TurnstileWidget
              ref={turnstileRef}
              className={styles.turnstile}
              action="signup"
              // Non-blocking, and narrow: a fresh token answers the "you have
              // not solved it yet" prompt and nothing else. Clearing the others
              // would wipe the rejection message the moment managed mode
              // silently re-issues a token, leaving no instruction to resubmit,
              // and would erase a service-fault message a token cannot fix. It
              // deliberately does NOT gate the button.
              onTokenChange={(token) => {
                if (!token) return;
                setSubmitError((current) =>
                  current === TURNSTILE_REQUIRED_MESSAGE ? undefined : current,
                );
              }}
            />
            {submitError && (
              <p id={SUBMIT_ERROR_ID} role="alert" className={styles.submitError}>
                {submitError}
              </p>
            )}
            <button
              type="submit"
              // TURNSTILE_MESSAGE_ID exists only once the widget has something
              // to say (script blocked / still loading), and a dangling
              // reference is ignored until then. It has to be here regardless:
              // that message is the only explanation for a submit that cannot
              // work, and it gates account creation.
              aria-describedby={[TURNSTILE_MESSAGE_ID, submitError ? SUBMIT_ERROR_ID : '']
                .filter(Boolean)
                .join(' ')}
            >
              Create Account
            </button>
          </div>
          <p className={styles.signInPrompt}>
            Already have an account? <Link to="/signin">Sign in</Link>
          </p>
        </form>
      </div>
      <div className={styles.imageContainer}>
        <img src={accountImage} alt="Person sitting with a laptop" />
        <AuthFooter />
      </div>
    </div>
  );
};

export default CreateAccount;